import { useEffect, useMemo, useState } from "react";
import { useStore } from "@features/chat";
import { Dialog } from "@shared/ui/bits";
import { Input } from "@shared/ui/input";
import { IconDirects, IconTextChannel, IconAnnouncementChannel } from "@shared/ui/icons";
import { ServerTile } from "@entities/server";
import { search, type Target } from "../lib/search";
import { directName } from "@features/group-dm";
import { isFeed } from "@entities/channel";

/**
 * Быстрый переход — как Ctrl+K в Discord: набрал часть имени, Enter, и ты там.
 * Каналы всех серверов, а не только открытого: ради чужого сервера он и
 * нужен, по своему и так видно список слева.
 */
export function QuickSwitcher({
  onPick,
  onClose,
}: {
  onPick: (target: Target) => void;
  onClose: () => void;
}) {
  const store = useStore();
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState(0);

  // Каналы серверов, куда ещё не заходили, — дозагрузить: иначе их не найти.
  useEffect(() => {
    for (const server of store.servers) {
      if (!store.states[server.id]) void store.loadServer(server.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const targets = useMemo<Target[]>(
    () => [
      ...store.directs.map((direct) => ({
        kind: "direct" as const,
        id: direct.channel_id,
        name: directName(direct),
        handle: direct.recipient?.handle ?? "группа",
      })),
      // Друзья без переписки — после переписок: выбор начнёт её, как «начать беседу» в Discord.
      ...store.relationships
        .filter(
          (user) =>
            user.relationship === "friend" && !store.directs.some((direct) => direct.recipient?.id === user.id),
        )
        .map((user) => ({
          kind: "person" as const,
          id: user.id,
          name: user.display_name || user.username,
          handle: user.handle,
        })),
      ...store.servers.map((server) => ({
        kind: "server" as const,
        id: server.id,
        name: server.name,
        iconId: server.icon_id,
      })),
      ...store.servers.flatMap((server) =>
        (store.states[server.id]?.channels ?? [])
          .filter((channel) => isFeed(channel.channel_type))
          .map((channel) => ({
            kind: "channel" as const,
            id: channel.id,
            name: channel.name,
            serverId: server.id,
            serverName: server.name,
            announcement: channel.channel_type === "announcement",
          })),
      ),
    ],
    [store.directs, store.relationships, store.servers, store.states],
  );
  const found = search(targets, query);
  const at = Math.min(chosen, Math.max(0, found.length - 1));

  return (
    <Dialog title="Куда отправимся?" onClose={onClose}>
      <Input
        value={query}
        placeholder="Канал, сервер, переписка или друг"
        aria-label="Куда перейти"
        onChange={(event) => {
          setQuery(event.target.value);
          setChosen(0);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            const step = event.key === "ArrowDown" ? 1 : -1;
            setChosen((at + step + found.length) % Math.max(1, found.length));
          }
          if (event.key === "Enter" && found[at]) {
            event.preventDefault();
            onPick(found[at]);
          }
        }}
      />

      <div className="switcher-list" role="listbox" aria-label="Найдено">
        {found.map((target, index) => (
          <button
            key={`${target.kind}-${target.id}`}
            type="button"
            role="option"
            aria-selected={index === at}
            className={`switcher-item ${index === at ? "on" : ""}`}
            onMouseMove={() => setChosen(index)}
            onClick={() => onPick(target)}
          >
            <span className="switcher-mark">
              {target.kind === "server" ? (
                <ServerTile name={target.name} id={target.id} iconId={target.iconId} size={20} />
              ) : target.kind === "channel" ? (
                target.announcement ? (
                  <IconAnnouncementChannel size={15} />
                ) : (
                  <IconTextChannel size={15} />
                )
              ) : (
                <IconDirects size={15} />
              )}
            </span>
            <span className="switcher-name ellipsis">{target.name}</span>
            <span className="switcher-where ellipsis">
              {target.kind === "channel"
                ? target.serverName
                : target.kind === "direct"
                  ? target.handle
                  : target.kind === "person"
                    ? "начать беседу"
                    : "сервер"}
            </span>
          </button>
        ))}
        {found.length === 0 && <div className="switcher-none">Ничего не нашлось</div>}
      </div>

      <div className="switcher-hint">
        В начале <b>#</b> — только каналы, <b>@</b> — переписки и друзья, <b>*</b> — серверы. ↑↓ и Enter.
      </div>
    </Dialog>
  );
}
